import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import Dialog from "@/components/ui/Dialog";
import { CatalogPlan, cancelSubscription } from "@/services/stripe";
import { formatCount, formatDay, formatMoney } from "@/utils/billingFormat";

interface Props {
  isOpen: boolean;
  currentPlan: string;
  endsAt: string | null;
  hasTopUps: boolean;
  cheaperPlan: CatalogPlan | null;
  onClose: () => void;
  onCancelled: () => void;
  onSwitchInstead: (planName: string) => void;
}

const REASONS = [
  { id: "too_expensive", label: "It costs too much" },
  { id: "not_needed", label: "I don't need it right now" },
  { id: "didnt_work", label: "It didn't work on the sites I need" },
  { id: "missing_feature", label: "It's missing something I need" },
  { id: "switching", label: "I'm moving to another tool" },
  { id: "other", label: "Something else" },
];

export default function CancelPlanDialog({ isOpen, currentPlan, endsAt, hasTopUps, cheaperPlan, onClose, onCancelled, onSwitchInstead }: Props) {
  const [reason, setReason] = useState<string | null>(null);
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setReason(null);
      setDetails("");
      setError(null);
    }
  }, [isOpen]);

  const cancel = async () => {
    setSaving(true);
    setError(null);
    try {
      await cancelSubscription({ reason: reason ?? undefined, details: details.trim() || undefined });
      onCancelled();
    } catch (err) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Couldn't cancel your plan. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={() => !saving && onClose()} title={`Cancel your ${currentPlan} plan`} width="md">
      <div className="space-y-5">
        <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
          <p>
            You keep {currentPlan} and its credits until{" "}
            <span className="font-medium text-gray-900 dark:text-white">{endsAt ? formatDay(endsAt) : "the end of this billing cycle"}</span>. After that your
            account moves to the Free plan and you won&apos;t be charged again.
          </p>
          {hasTopUps && <p>Unused top-up credits and bandwidth stay on your account until they expire.</p>}
          <p>You can undo this from the Billing page any time before then.</p>
        </div>

        {cheaperPlan && (
          <div className="rounded-lg border border-gray-200 dark:border-white/10 p-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-gray-600 dark:text-gray-300 flex-1">
              Want to pay less instead? <span className="font-medium text-gray-900 dark:text-white">{cheaperPlan.name}</span> is{" "}
              {formatMoney(cheaperPlan.priceCents, cheaperPlan.currency)} a month with {formatCount(cheaperPlan.includedCredits)} credits, from your next renewal.
            </p>
            <button
              onClick={() => onSwitchInstead(cheaperPlan.name)}
              disabled={saving}
              className="shrink-0 h-9 px-3 rounded-md border border-gray-300 dark:border-white/15 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer"
            >
              Switch to {cheaperPlan.name}
            </button>
          </div>
        )}

        <fieldset>
          <legend className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Why are you cancelling? <span className="font-normal text-gray-400">(optional)</span>
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {REASONS.map(r => (
              <button
                key={r.id}
                type="button"
                aria-pressed={reason === r.id}
                onClick={() => setReason(reason === r.id ? null : r.id)}
                className={`px-3 py-1.5 rounded-full border text-xs transition cursor-pointer ${
                  reason === r.id
                    ? "border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-200"
                    : "border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-white/20"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {reason && (
            <textarea
              value={details}
              onChange={e => setDetails(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Anything we should know? (optional)"
              className="mt-3 w-full rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            />
          )}
        </fieldset>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="h-10 px-4 text-sm font-medium text-white bg-primary hover:bg-blue-700 rounded-md transition cursor-pointer"
          >
            Keep my plan
          </button>
          <button
            onClick={cancel}
            disabled={saving}
            className="h-10 px-4 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
          >
            {saving && <Icon icon="eos-icons:loading" />}
            Cancel plan
          </button>
        </div>
      </div>
    </Dialog>
  );
}
